import { TLatLng } from "../types/map";

/**
 * A legacy Geozone row keeps its first 10 points in latitude1..10 / longitude1..10 and any
 * further points in `vertices`, a "lat/lon,lat/lon,..." string (see GTS Geozone.getGeoPoints).
 * Zones loaded in bulk may keep every point in `vertices` and leave the columns at 0.
 */
export const GEOZONE_POINT_COLUMNS = 10;

type TGeozonePointSource = {
  vertices?: string | null;
  [column: string]: unknown;
};

const isValidPoint = (latitude: number, longitude: number) =>
  Number.isFinite(latitude) &&
  Number.isFinite(longitude) &&
  Math.abs(latitude) <= 90 &&
  Math.abs(longitude) <= 180 &&
  !(latitude === 0 && longitude === 0);

export const parseGeozoneVertices = (vertices?: string | null): TLatLng[] =>
  (vertices || "")
    .split(",")
    .map((pair) => pair.trim().split("/"))
    .filter((parts) => parts.length === 2 && parts.every((part) => part !== ""))
    .map(([latitude, longitude]) => ({
      latitude: Number(latitude),
      longitude: Number(longitude),
    }))
    .filter((point) => isValidPoint(point.latitude, point.longitude));

/** Every point of the zone: the columns first, then `vertices`. */
export const getGeozonePoints = (geozone?: TGeozonePointSource): TLatLng[] => {
  if (!geozone) return [];
  const columnPoints: TLatLng[] = [];
  for (let i = 1; i <= GEOZONE_POINT_COLUMNS; i++) {
    const latitude = Number(geozone[`latitude${i}`]);
    const longitude = Number(geozone[`longitude${i}`]);
    if (isValidPoint(latitude, longitude)) {
      columnPoints.push({ latitude, longitude });
    }
  }
  return [...columnPoints, ...parseGeozoneVertices(geozone.vertices)];
};

/** The `vertices` value to save: the points that do not fit in the columns, or null. */
export const formatGeozoneVertices = (locs: TLatLng[]): string | null => {
  const overflow = locs
    .slice(GEOZONE_POINT_COLUMNS)
    .filter((loc) => !!loc)
    .map((loc) => `${loc.latitude.toFixed(5)}/${loc.longitude.toFixed(5)}`);
  return overflow.length > 0 ? overflow.join(",") : null;
};
